<template>
  <div class="main">
    <div class="title">
      <h2>{{ title }}</h2>
    </div>
    <div
      ref="content"
      class="content"
    >
      <Spin
        v-if="loading"
        fix
      >
        <Icon
          type="ios-loading"
          size="18"
          class="spin"
        />
        <div>Loading</div>
      </Spin>
      <template v-else>
        <Tabs
          v-model="currentTabPane"
          name="goldenQuery"
          :animated="false"
          class="golden-query-tab"
        >
          <TabPane
            tab="goldenQuery"
            name="Official"
            label="Official"
          />
          <TabPane
            tab="goldenQuery"
            name="Community"
            label="Community"
          />
          <template v-if="currentTabPane === 'Official'">
            <div
              v-if="!list.official.length"
              class="no-data"
            >
              There isn't any registered Official Golden Query.
            </div>
            <Table
              v-else
              :width="contentWidth"
              :max-height="contentHeight"
              :columns="columns.slice(0, -1)"
              :data="list.official"
              class="table"
            >
              <template
                slot="title"
                slot-scope="{ row }"
              >
                <Tooltip
                  placement="top-start"
                  max-width="600"
                >
                  <p>{{ row.name }}</p>
                  <div slot="content">
                    <p class="query-description">
                      {{ row.description }}
                    </p>
                  </div>
                </Tooltip>
              </template>
              <template
                slot="author"
                slot-scope="{ row }"
              >
                <Tooltip
                  :content="row.author.email"
                  placement="top"
                >
                  <p>{{ row.author.name }}</p>
                </Tooltip>
              </template>
              <template
                slot="tags"
                slot-scope="{ row }"
              >
                <p
                  v-for="tag in row.categories"
                  :key="tag"
                >
                  {{ tag }}
                </p>
              </template>
              <template
                slot="status"
                slot-scope="{ row }"
              >
                <Tooltip
                  :content="row.status"
                  placement="top"
                  class="query-status"
                >
                  <i
                    :class="queryStatusIcon(row.status)"
                  />
                </Tooltip>
              </template>
            </Table>
          </template>
          <template v-else>
            <div
              v-if="!list.community.length"
              class="no-data"
            >
              There isn't any registered Community Query.
            </div>
            <Table
              v-else
              :width="contentWidth"
              :max-height="contentHeight"
              :columns="columns"
              :data="list.community"
              class="table"
            >
              <template
                slot="title"
                slot-scope="{ row }"
              >
                <Tooltip
                  placement="top-start"
                  max-width="600"
                >
                  <p>{{ row.name }}</p>
                  <div slot="content">
                    <p class="query-description">
                      {{ row.description }}
                    </p>
                  </div>
                </Tooltip>
              </template>
              <template
                slot="author"
                slot-scope="{ row }"
              >
                <Tooltip
                  :content="row.author.email"
                  placement="top"
                >
                  <p>{{ row.author.name }}</p>
                </Tooltip>
              </template>
              <template
                slot="tags"
                slot-scope="{ row }"
              >
                <p
                  v-for="tag in row.tags"
                  :key="tag"
                >
                  {{ tag }}
                </p>
              </template>
              <template
                slot="status"
                slot-scope="{ row }"
              >
                <Tooltip
                  :content="row.status"
                  placement="top"
                  class="query-status"
                >
                  <i
                    :class="queryStatusIcon(row.status)"
                  />
                </Tooltip>
              </template>
              <template
                slot="action"
                slot-scope="{ row }"
              >
                <Tooltip
                  content="Generate File"
                  placement="top"
                >
                  <i
                    class="fas fa-download action-icon"
                    @click="downloadQueryFile(row)"
                  />
                </Tooltip>
              </template>
            </Table>
          </template>
        </Tabs>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import moment from 'moment-timezone'

export default {
  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      contentWidth: 0,
      contentHeight: 0,
      loading: true,
      list: {
        official: [],
        community: []
      },
      columns: [
        {
          title: 'ID',
          key: 'id',
          sortable: true,
          sortType: 'asc',
          width: '48'
        },
        {
          title: 'Title',
          key: 'name',
          slot: 'title',
          sortable: true
        },
        {
          title: 'Author',
          key: 'author',
          slot: 'author',
          width: '144',
          sortable: true
        },
        {
          title: 'Tags',
          key: 'tags',
          slot: 'tags',
          width: '136',
          sortable: true
        },
        {
          title: 'Status',
          key: 'status',
          slot: 'status',
          width: '100',
          align: 'center',
          sortable: true
        },
        {
          title: 'Action',
          slot: 'action',
          width: '100',
          align: 'center'
        }
      ],
      currentTabPane: 'Official'
    }
  },

  mounted () {
    window.addEventListener('resize', this.getContentMeasures)
    this.getContentMeasures()
    this.fetchGoldenQueries()
  },

  methods: {
    /**
     * Gets the width and height of the content according with the browser measures.
     */
    getContentMeasures () {
      if (this.$refs.content) {
        this.contentWidth = this.$refs.content.offsetWidth
        this.contentHeight = this.$refs.content.offsetHeight
      }
    },

    /**
     * Fetches the Official Golden Queries and Community Queries.
     */
    async fetchGoldenQueries () {
      this.loading = true

      // Fetch golden query (official) from global variable.
      const [officialQueries, communityQueries] = await Promise.all([
        axios.get(`${process.env.VUE_APP_API_URI}/golden-query/sql-queries`),
        axios.get(`${process.env.VUE_APP_API_URI}/golden-query/templates`)
      ])

      this.list.official = officialQueries.data.map(query => {
        this.$set(query, 'status', query.status || 'active')
        this.$set(query, 'tags', query.categories || [])
        this.$set(query, 'lines', query.lines)
        this.$set(query, 'params', query.params)
        return query
      })

      // Fetch query templates from mysql database
      this.list.community = communityQueries.data.map(communityQuery => {
        const query = {}
        const queryParams = JSON.parse(communityQuery.parameters)
        const queryTags = communityQuery.categories.split(',')
        query.id = communityQuery.id
        query.name = communityQuery.title
        query.description = communityQuery.description
        query.tags = queryTags || []
        query.date = communityQuery['created_at']
        query.author = { email: communityQuery['author_email'], name: communityQuery['author_name'] }
        query.params = queryParams
        query.lines = communityQuery['query_template']
        query.status = query.status || 'active'
        return query
      })

      this.loading = false
    },

    /**
     * Returns an icon class based on the query status: Active, Maintenance, Inactive
     * @param {String} status
     * @return {String}
     */
    queryStatusIcon (status) {
      return status === 'inactive'
        ? 'fas fa-ban action-icon inactive'
        : (status === 'maintenance'
          ? 'fas fa-exclamation-triangle action-icon hold'
          : 'fas fa-check-circle action-icon active'
        )
    },

    /**
     * Generates the query file and download it automatically.
     * @param {Object} query
     */
    downloadQueryFile (query) {
      const download = document.createElement('a')
      const filename = `${_.snakeCase(query.name)}.sql`
      const queryJson = {
        title: query.name,
        description: query.description,
        categories: query.tags,
        author: query.author,
        date: moment(query.date).format('MM-DD-YYYY'),
        params: query.params
      }
      const queryContent = `/* QUERY_DETAILS_BEGIN\n` +
          `${JSON.stringify(queryJson, null, '\t')}\n` +
          `QUERY_DETAILS_END */\n` +
          `${query.lines}`

      download.setAttribute('href', 'data:text/x-sql;charset=utf-8,' + encodeURIComponent(queryContent))
      download.setAttribute('download', filename)
      download.style.display = 'none'
      document.body.appendChild(download)
      download.click()
      document.body.removeChild(download)
    }
  }
}
</script>

<style scoped>
  .main {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .title {
    margin-bottom: 8px;
    padding-bottom: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 1px solid var(--color-gray-border);
  }

  .title h2 {
    font-size: 16px;
    width: 100%;
  }

  .spin,
  .table >>> .ivu-table-cell .spin {
    animation: ani-demo-spin 1s linear infinite;
  }

  .action-icon {
    width: 24px;
    text-align: center;
    font-size: 16px;
    color: var(--color-black);
    cursor: pointer;
  }

  .action-icon.active {
    color: var(--color-green-lime);
  }

  .action-icon.hold {
    color: var(--color-yellow-dark);
  }

  .action-icon.inactive {
    color: var(--color-red-border);
  }

  .query-status {
    text-transform: capitalize;
  }

  .no-data {
    font-size: 24px;
    color: var(--color-gray);
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .query-description {
    white-space: initial;
    word-break: break-word;
  }
</style>
